/**
 * ClubAddReceipt — formularz dodawania paragonu (zdjęcie + kwota + data + lokal).
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clubApi, formatApiErrorDetail } from './clubApi';

const LOCATIONS = [
  { id: 'nowysacz', name: 'Nowy Sącz', icon: '🏔️' },
  { id: 'krakow', name: 'Kraków', icon: '🏰' },
  { id: 'grodek', name: 'Gródek nad Dunajcem', icon: '🌊' },
];

async function compressImage(file, maxSize = 1400, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const k = width > height ? maxSize / width : maxSize / height;
        width = Math.round(width * k);
        height = Math.round(height * k);
      }
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ClubAddReceipt() {
  const navigate = useNavigate();
  const [photo, setPhoto] = useState('');
  const [amount, setAmount] = useState('');
  const [visitDate, setVisitDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError('To musi być zdjęcie'); return; }
    try {
      const dataUrl = await compressImage(f);
      setPhoto(dataUrl);
      setError('');
    } catch {
      setError('Nie udało się przetworzyć zdjęcia');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!photo) { setError('Wybierz zdjęcie paragonu'); return; }
    if (!location) { setError('Wybierz lokal'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Wpisz kwotę rachunku'); return; }
    setBusy(true);
    try {
      const { data } = await clubApi.post('/receipts', {
        photo, amount: amt, visit_date: visitDate, location, comment,
      });
      setSuccess(data);
      setPhoto(''); setAmount(''); setComment('');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="club-shell">
        <div className="club-brand">
          <div className="club-brand-logo" style={{ background: 'linear-gradient(135deg, #10b981, #047857)' }}>✅</div>
          <h1 className="club-brand-name" style={{ color: '#10b981' }}>Wysłane!</h1>
          <div className="club-brand-sub">Czekamy na weryfikację</div>
        </div>
        <div className="club-card gold" data-testid="club-receipt-success">
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--club-text)' }}>
            Twój paragon trafił do nas i zostanie sprawdzony przez managera.
            <br /><br />
            Po akceptacji punkty trafią na Twoje konto automatycznie.
            {success.bonus_points > 0 && (
              <div style={{ marginTop: 14, padding: 12, background: 'rgba(212,175,55,0.10)', borderRadius: 10 }}>
                🎉 <b>+{success.bonus_points} pkt</b> za pierwszy paragon — sprawdź dashboard!
              </div>
            )}
          </div>
        </div>
        <button className="club-btn club-btn-primary" onClick={() => navigate('/mrjerryclub/dashboard')}>
          🏠 Wróć do startu
        </button>
        <button className="club-btn club-btn-ghost" style={{ marginTop: 8 }} onClick={() => setSuccess(null)}>
          ➕ Dodaj kolejny paragon
        </button>
      </div>
    );
  }

  return (
    <div className="club-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.05)',
            border: '1px solid var(--club-border-soft)', color: 'var(--club-text)',
            cursor: 'pointer', fontSize: 18,
          }}
          data-testid="club-back-btn"
        >‹</button>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--club-text)' }}>Dodaj paragon</div>
          <div style={{ fontSize: 12, color: 'var(--club-text-muted)' }}>Zdobądź punkty za każdą wizytę</div>
        </div>
      </div>

      <form onSubmit={submit} data-testid="club-receipt-form">
        {error && <div className="club-alert club-alert-error">⚠️ {error}</div>}

        {/* Photo */}
        <label className="club-label">Zdjęcie paragonu</label>
        {photo ? (
          <div style={{ position: 'relative' }}>
            <img src={photo} alt="paragon" className="club-photo-preview" data-testid="club-photo-preview" />
            <button
              type="button"
              onClick={() => setPhoto('')}
              style={{
                position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14,
              }}
              data-testid="club-photo-remove"
            >✕</button>
          </div>
        ) : (
          <label htmlFor="club-receipt-photo-input" className="club-photo-zone" data-testid="club-photo-zone">
            <div style={{ fontSize: 32, marginBottom: 8 }}>📸</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--club-gold)' }}>Zrób zdjęcie lub wybierz z galerii</div>
            <div className="club-help" style={{ marginTop: 6 }}>JPG/PNG. Zostanie skompresowane automatycznie.</div>
            <input
              id="club-receipt-photo-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPhoto}
              style={{ display: 'none' }}
              data-testid="club-photo-input"
            />
          </label>
        )}
        <div style={{ height: 16 }} />

        {/* Amount */}
        <label className="club-label">Kwota rachunku (PLN)</label>
        <input
          type="number"
          step="0.01"
          min="0"
          className="club-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0,00"
          inputMode="decimal"
          data-testid="club-receipt-amount"
        />
        <div style={{ height: 12 }} />

        {/* Date */}
        <label className="club-label">Data wizyty</label>
        <input
          type="date"
          className="club-input"
          value={visitDate}
          onChange={(e) => setVisitDate(e.target.value)}
          max={new Date().toISOString().slice(0, 10)}
          data-testid="club-receipt-date"
        />
        <div style={{ height: 12 }} />

        {/* Location */}
        <label className="club-label">Lokal</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
          {LOCATIONS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setLocation(l.id)}
              data-testid={`club-receipt-location-${l.id}`}
              style={{
                padding: 14, borderRadius: 12, textAlign: 'left',
                background: location === l.id ? 'rgba(212,175,55,0.15)' : 'rgba(0,0,0,0.3)',
                border: `1px solid ${location === l.id ? 'var(--club-gold)' : 'var(--club-border-soft)'}`,
                color: location === l.id ? 'var(--club-gold)' : 'var(--club-text)',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{ fontSize: 22 }}>{l.icon}</span> {l.name}
            </button>
          ))}
        </div>
        <div style={{ height: 12 }} />

        {/* Comment */}
        <label className="club-label">Komentarz (opcjonalnie)</label>
        <textarea
          className="club-input"
          rows={3}
          maxLength={500}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="np. wizyta w grupie 4 osób, świetna obsługa…"
          data-testid="club-receipt-comment"
        />
        <div style={{ height: 20 }} />

        <button
          type="submit"
          className="club-btn club-btn-primary"
          disabled={busy || !photo || !amount || !location}
          data-testid="club-receipt-submit"
        >
          {busy ? 'Wysyłanie…' : '📤 Wyślij paragon do weryfikacji'}
        </button>
      </form>
    </div>
  );
}
